<template>
  <div ref="wrapper">
    <div class="empty:tw-hidden" @click="handleToggleDropdown">
      <slot name="trigger"> </slot>
    </div>

    <div
      v-show="isScopedDropdownOpen"
      class="empty:tw-hidden"
      @click="handleToggleDropdown"
    >
      <slot name="custom-trigger-open"></slot>
    </div>

    <div
      v-show="!isScopedDropdownOpen"
      class="empty:tw-hidden"
      @click="handleToggleDropdown"
    >
      <slot name="custom-trigger-closed"></slot>
    </div>

    <div
      v-show="isScopedDropdownOpen"
      @click.capture="handleMenuClick"
      @mouseover="handleMenuHover"
      @mouseleave="scheduleSubmenuClose"
      @keydown="handleMenuKeydown"
    >
      <slot name="menu"> </slot>
      <div v-for="id in submenuIdList" v-show="activeSubmenu === id" :key="id">
        <slot :name="`submenu-${id}`"> </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'

import { useDropdown } from '@app/shared/pinia/dropdown'
import { useDropdownSubmenu } from '@app/shared/vue/composables/use-dropdown-submenu'
import getPiniaInstance from '@app/shared/pinia/instance'

interface Props {
  dropdownId: string
  isDisabled?: boolean
  // Comma-separated submenu ids, so every submenu slot outlet can be
  // rendered up front: renaming a single dynamic <slot> in place does not
  // reassign the slotted content, so switching submenus needs one static
  // outlet per id toggled with v-show.
  submenuIds?: string
}

const props = defineProps<Props>()

const submenuIdList = computed(() =>
  (props.submenuIds || '').split(',').filter(Boolean)
)

const wrapper = ref<HTMLElement | null>(null)

onClickOutside(wrapper, () => {
  if (isScopedDropdownOpen.value) {
    handleCloseDropdown()
  }
})

const dropdownStore = useDropdown(getPiniaInstance())

const isScopedDropdownOpen = computed(
  () => dropdownStore.currentDropdownId === props.dropdownId
)

const {
  activeSubmenu,
  handleMenuClick,
  handleMenuHover,
  handleMenuKeydown,
  resetSubmenu,
  scheduleSubmenuClose,
} = useDropdownSubmenu({ syncTriggerAria: true })

watch(isScopedDropdownOpen, (isOpen) => {
  if (!isOpen) {
    resetSubmenu()
  }
})

function handleCloseDropdown() {
  dropdownStore.closeDropdown()
}

function handleToggleDropdown() {
  if (!props.isDisabled) {
    dropdownStore.toggleDropdown(props.dropdownId)
  }
}
</script>
